import { FC, useEffect, useState } from "react";
import { Button, Form, Input, List, message, Modal, Row, Space, Typography } from "../../design-system";
import { CreateVendorRequest, Vendor } from "../../types/VendorTypes";
import { createVendor, loadVendorSuggestions } from "../../common/api/VendorClient";
import { handleError } from "../../common/ErrorHandling";
import { CompanyImage } from "../../common/components/CompanyImage";
import {
    clean,
    FIELD_MAX,
    LogoMode,
    logoUrlFor,
    VendorLogoField,
} from "./components/VendorLogoField";

export interface NewVendorModalProps {
    /** Seed for the Name field, usually whatever the user typed in the picker. */
    initialName?: string;
    /** Called with the vendor the server created. */
    onCreate: (vendor: Vendor) => void;
    /** Called when the user picks one of the similar existing vendors instead. */
    onUseExisting: (vendor: Vendor) => void;
    onCancel: () => void;
}

interface NewVendorFormValues extends Omit<CreateVendorRequest, "hero"> {
    customLogo?: string;
}

const SIMILAR_LIMIT = 5;
const SIMILAR_DEBOUNCE_MS = 300;
const NAME_MIN = 2;

/**
 * Super-admin modal to create a vendor in the current organization without
 * leaving the screen the user is on. Vendors are per organization, so the
 * "similar" list only shows this organization's vendors; the server rejects
 * an exact (case-insensitive) name match within the organization.
 */
export const NewVendorModal: FC<NewVendorModalProps> = ({
    initialName,
    onCreate,
    onUseExisting,
    onCancel,
}) => {
    const [form] = Form.useForm<NewVendorFormValues>();
    const [loading, setLoading] = useState(false);
    const [similar, setSimilar] = useState<Vendor[]>([]);
    const [logoMode, setLogoMode] = useState<LogoMode>("default");
    const watchedName = Form.useWatch("name", form);
    const watchedUrl = Form.useWatch("url", form);
    const watchedCustomLogo = Form.useWatch("customLogo", form);
    const trimmedName = (watchedName ?? "").trim();

    const activeLogo = logoUrlFor(logoMode, watchedUrl, watchedCustomLogo);

    useEffect(() => {
        if (trimmedName.length < NAME_MIN) {
            setSimilar([]);
            return;
        }
        let cancelled = false;
        const ac = new AbortController();
        const timer = setTimeout(() => {
            loadVendorSuggestions(trimmedName, { signal: ac.signal })
                .then((vendors) => {
                    if (!cancelled) setSimilar(vendors.slice(0, SIMILAR_LIMIT));
                })
                .catch(() => {});
        }, SIMILAR_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            ac.abort();
        };
    }, [trimmedName]);

    const handleSubmit = async (values: NewVendorFormValues) => {
        setLoading(true);
        try {
            const vendor = await createVendor({
                name: values.name.trim(),
                url: clean(values.url),
                hero: activeLogo,
            });
            message.success(`Vendor "${vendor.name}" created`);
            onCreate(vendor);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Create vendor"
            open
            footer={null}
            zIndex={1050}
            destroyOnClose
            maskClosable={!loading}
            closable={!loading}
            onCancel={onCancel}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ name: initialName ?? "" }}
                onFinish={handleSubmit}
                disabled={loading}
            >
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                        {
                            required: true,
                            whitespace: true,
                            min: NAME_MIN,
                            message: `Enter a vendor name (at least ${NAME_MIN} characters)`,
                        },
                        { max: FIELD_MAX, message: `Keep the name under ${FIELD_MAX} characters` },
                    ]}
                >
                    <Input autoFocus placeholder="Vendor name" />
                </Form.Item>

                {similar.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong>Similar existing vendors</Typography.Text>
                        <br />
                        <Typography.Text type="secondary">
                            Check the list below before creating a duplicate.
                        </Typography.Text>
                        <List
                            size="small"
                            dataSource={similar}
                            renderItem={(vendor) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="use"
                                            type="link"
                                            size="small"
                                            onClick={() => onUseExisting(vendor)}
                                        >
                                            Use this vendor
                                        </Button>,
                                    ]}
                                >
                                    <Space>
                                        <CompanyImage
                                            logo={vendor.hero}
                                            size="xs"
                                            name={vendor.name}
                                        />
                                        <span>{vendor.name}</span>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </div>
                )}

                <Form.Item
                    label="Website"
                    name="url"
                    rules={[
                        {
                            max: FIELD_MAX,
                            message: `Keep the website under ${FIELD_MAX} characters`,
                        },
                    ]}
                >
                    <Input placeholder="vendor.com" />
                </Form.Item>

                <VendorLogoField
                    vendorName={trimmedName}
                    website={watchedUrl}
                    customLogo={watchedCustomLogo}
                    mode={logoMode}
                    onModeChange={setLogoMode}
                />

                <Form.Item style={{ marginBottom: 0 }}>
                    <Row justify="end">
                        <Space size={16}>
                            <Button onClick={onCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Create vendor
                            </Button>
                        </Space>
                    </Row>
                </Form.Item>
            </Form>
        </Modal>
    );
};
